import React from 'react';
import { Label } from '@client/components/ui/label';
import { Switch } from '@client/components/ui/switch';
import { AdvisorField } from '@/components/advisor-settings/AdvisorField';
import { AuthInputTextField, AuthInputNumberField, AuthSeparator } from '@/components';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

interface DefaultsTabProps {
  advisorState: AdvisorProfile;
  errors: Partial<Record<keyof AdvisorProfile, boolean>>;
  handleAdvisorStateChange: (
    advisorKey: keyof AdvisorProfile,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOverviewSlideChange: (checked: boolean) => void;
}

export function DefaultsTab({
  advisorState,
  errors,
  handleAdvisorStateChange,
  handleOverviewSlideChange,
}: DefaultsTabProps) {
  const { flags } = useFeatureFlags();

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        className="sm:col-span-6"
        text="Healthcare Inflation Settings"
      />
      <AuthInputNumberField
        id="defaultHealthcareInflationPercent"
        description="The default healthcare inflation rate used to calculate the client's healthcare costs."
        label="Default Healthcare Inflation Rate"
        value={advisorState.defaultHealthcareInflationPercent}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationPercent')}
        placeholder="e.g. 5.4"
        unit="%"
        error={errors.defaultHealthcareInflationPercent}
        className="mt-0 sm:col-span-6"
        min={0}
        step={0.001}
        max={30}
        width="150px"
        required={false}
      />
      <AuthInputTextField
        id="defaultHealthcareInflationSourceText"
        label="Default Healthcare Inflation Source Text"
        description="Displayed text for the healthcare inflation source citation."
        value={advisorState.defaultHealthcareInflationSourceText ?? ''}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationSourceText')}
        className="mt-0 sm:col-span-6"
        required={Boolean(advisorState.defaultHealthcareInflationPercent)}
        error={errors.defaultHealthcareInflationSourceText}
      />
      <AuthInputTextField
        id="defaultHealthcareInflationSourceUrl"
        label="Default Healthcare Inflation Source Link"
        description="URL for the healthcare inflation rate source."
        value={advisorState.defaultHealthcareInflationSourceUrl ?? ''}
        onChange={handleAdvisorStateChange('defaultHealthcareInflationSourceUrl')}
        className="mt-0 sm:col-span-6"
        required={false}
      />
      {flags.overviewSlide && (
        <>
          <AuthSeparator
            className="sm:col-span-6"
            text="Client Onboarding Settings"
          />
          <AdvisorField
            label="Show Overview Slide"
            description="When enabled, clients will see an overview slide summarizing their plan before viewing detailed results."
          >
            <div className="flex items-center space-x-3">
              <Switch
                id="defaultClientOnboardingOverviewSlide"
                checked={advisorState.defaultClientOnboardingOverviewSlide ?? true}
                onCheckedChange={handleOverviewSlideChange}
                className="data-[state=checked]:bg-primary dark:data-[state=checked]:bg-primary"
              />
              <Label
                htmlFor="defaultClientOnboardingOverviewSlide"
                className="text-sm text-gray-700"
              >
                {(advisorState.defaultClientOnboardingOverviewSlide ?? true)
                  ? 'Enabled'
                  : 'Disabled'}
              </Label>
            </div>
          </AdvisorField>
        </>
      )}
    </div>
  );
}
